/**
 * Professional Filters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 */
import React from 'react';
import { InlineFilterBar, InlineMultiSelect, InlineToggles } from '../../../components/common';
import { useFilterState } from '../../../hooks/useFilterState';
import { ProfessionalType, PROFESSIONAL_TYPE_LABELS } from '../../../types/professional';

interface ProfessionalFiltersProps {
  onFilter: (filters: ProfessionalFiltersData) => void;
  loading?: boolean;
  total?: number;
}

export interface ProfessionalFiltersData {
  searchTerm: string;
  status: string[];
  professionalTypes: string[];
  isExaminer: boolean;
}

const STATUS_OPTIONS = ['Ativo', 'Inativo', 'Suspenso', 'Bloqueado', 'Pendente'].map((s) => ({
  value: s,
  label: s,
}));
const TYPE_OPTIONS = Object.values(ProfessionalType).map((t) => ({
  value: t,
  label: PROFESSIONAL_TYPE_LABELS[t],
}));

const defaultValues: ProfessionalFiltersData = {
  searchTerm: '',
  status: ['Ativo'],
  professionalTypes: [],
  isExaminer: false,
};

const ProfessionalFilters: React.FC<ProfessionalFiltersProps> = ({
  onFilter,
  loading = false,
  total,
}) => {
  const { filters, setFilter, clearAll, activeChips } = useFilterState(defaultValues, {
    onFilter,
    applyOnMount: true,
    live: true,
    labels: {
      status: 'Status',
      professionalTypes: 'Tipo',
    },
    formatValue: {
      status: (v: string[]) => v.join(', '),
      professionalTypes: (v: string[]) =>
        v.map((t) => PROFESSIONAL_TYPE_LABELS[t as ProfessionalType] ?? t).join(', '),
    },
  });

  return (
    <InlineFilterBar
      searchPlaceholder="Buscar por nome, email ou CPF..."
      searchValue={filters.searchTerm}
      onSearchChange={(v) => setFilter('searchTerm', v)}
      resultCount={total}
      resultNoun="profissionais"
      chips={activeChips}
      onClearAll={clearAll}
      loading={loading}
    >
      <InlineMultiSelect
        label="Status"
        value={filters.status}
        options={STATUS_OPTIONS}
        onChange={(v) => setFilter('status', v)}
        minWidth={140}
      />
      <InlineMultiSelect
        label="Tipo"
        value={filters.professionalTypes}
        options={TYPE_OPTIONS}
        onChange={(v) => setFilter('professionalTypes', v)}
        minWidth={150}
      />
      <InlineToggles
        disabled={loading}
        items={[
          {
            label: 'Examinador',
            checked: filters.isExaminer,
            onChange: (v) => setFilter('isExaminer', v),
          },
        ]}
      />
    </InlineFilterBar>
  );
};

export default ProfessionalFilters;
